import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setAddCollectionProductsModal } from "./AddCollectionProductsModalSlice";
import { type MultiValue, type OptionProps } from "react-select";
import AsyncSelect from "react-select/async";
import { useLazySearchProductsQuery } from "../../pages/searchPage/SearchSlice";
import { ProductIndexItemInterface } from "../../common/interfaces/apiV2/shared/ProductIndexItemInterface";
import { SearchProductsResult } from "../../common/api/search";
import { LanguageInterface } from "../../common/interfaces/shared/LanguageInterface";
import { JSX, useState } from "react";
import { AsyncSelectOptionInterface } from "../../common/interfaces/AsyncSelectOptionInterface";
import {
    addCollectionProductIds,
    useLazyGetCollectionsQuery,
    useUpdateCollectionMutation,
} from "../../pages/collectionsPage/CollectionsSlice";
import { useGetUserInfoQuery } from "../../app/rootReducer";
import { Modal, Button } from "@hatch-digital-nl/bravilor-storybook";
import { useStringTranslation } from "../../common/hooks/useStringTranslation";

export const AddCollectionProductsModalContainer = (): JSX.Element => {
    const dispatch = useAppDispatch();

    const { data: userData } = useGetUserInfoQuery();
    const { activeLanguage } = userData || {
        activeLanguage: null,
    };
    const { t } = useStringTranslation();

    const { collections } = useAppSelector((state) => state.collections);
    const { collectionId } = useAppSelector(
        (state) => state.addCollectionProductsModal,
    );

    const [trigger] = useLazySearchProductsQuery();
    const [collectionTrigger] = useLazyGetCollectionsQuery();

    const [updateCollectionTrigger] = useUpdateCollectionMutation();

    const [loading, setLoading] = useState<boolean>(false);
    const [typingTimeout, setTypingTimeout] = useState<
        number | ReturnType<typeof setTimeout>
    >(0);

    const paramObject = {
        collectionId: collectionId,
        collectionName:
            collections.find((item: { id: number }) => item.id === collectionId)
                ?.name || "",
        productIds:
            collections.find((item: { id: number }) => item.id === collectionId)
                ?.productIds || [],
    };

    const flattenSearchResults = (
        data: SearchProductsResult | undefined,
    ): ProductIndexItemInterface[] => {
        if (!data) {
            return [];
        }

        return [...data.machines, ...data.accessories, ...data.old_models];
    };

    const selectOptionMapper = (
        data: ProductIndexItemInterface[] | undefined,
        _activeLanguageOption: LanguageInterface,
    ): AsyncSelectOptionInterface[] => {
        let mappedData: AsyncSelectOptionInterface[] = [];

        if (data && data.length > 0) {
            data.forEach((item: ProductIndexItemInterface) => {
                const mappedItem = {
                    value: item.id,
                    label: item.label ?? "",
                    price: item.prices?.value,
                    identifier: item.identifier,
                };
                mappedData = [...mappedData, mappedItem];
            });
        }
        return mappedData;
    };

    const loadOptions = (
        inputValue: string,
        callback: (options: AsyncSelectOptionInterface[]) => void,
    ): void => {
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        if (inputValue && inputValue !== "" && inputValue.length > 2) {
            setTypingTimeout(
                setTimeout(function () {
                    trigger({
                        search: inputValue,
                    }).then((result) => {
                        if (activeLanguage) {
                            callback(
                                selectOptionMapper(
                                    flattenSearchResults(result.data),
                                    activeLanguage,
                                ),
                            );
                        }
                    });
                }, 1000),
            );
        }
    };

    const IndicatorsContainer = (): JSX.Element => {
        return (
            <div className="react-select-indicator">
                <img src="/images/magnifier.svg" alt="" />
            </div>
        );
    };

    const SelectOption = (
        props: OptionProps<AsyncSelectOptionInterface, true>,
    ): JSX.Element => {
        return (
            <label
                className={"c-products-list__sub-item__item is-active"}
                onClick={() => props.selectOption(props.data)}
            >
                <div className="c-products-list__sub-item__item__left">
                    <div>
                        <span>{props.label}</span>
                    </div>
                </div>
                <div className="c-products-list__sub-item__item__center">
                    <span>{props.data.identifier}</span>
                </div>
                <div className="c-products-list__sub-item__item__right">
                    <span>{props.data.price}</span>
                </div>
            </label>
        );
    };

    const handleClose = (): void => {
        dispatch(
            setAddCollectionProductsModal({
                showPopup: false,
            }),
        );
    };

    return (
        <Modal onClose={handleClose} loading={loading}>
            <header className="c-modal__header">
                <h2>{t("add-product")}</h2>
            </header>
            <div className="c-modal__body">
                <p>
                    {t("do-you-want-to-add-another-product-to-this-collection")}
                </p>

                <div className="c-modal__body__input">
                    <div className="c-modal__body__input__label">
                        <label>{t("search-for-products")}</label>
                        <span>{t("select-one-or-more-products")}</span>
                    </div>

                    <AsyncSelect<AsyncSelectOptionInterface, true>
                        className="react-select-container"
                        isMulti={true}
                        components={{
                            IndicatorsContainer,
                            Option: SelectOption,
                        }}
                        loadOptions={loadOptions}
                        cacheOptions
                        onChange={(
                            selectedOptions: MultiValue<AsyncSelectOptionInterface>,
                        ) => {
                            selectedOptions.forEach(
                                (
                                    selectedOption: AsyncSelectOptionInterface,
                                ) => {
                                    dispatch(
                                        addCollectionProductIds({
                                            collectionId: collectionId,
                                            productId: selectedOption.value,
                                        }),
                                    );
                                },
                            );
                        }}
                        styles={{
                            multiValueLabel: (styles) => ({
                                ...styles,
                                backgroundColor: "#F6F6F6",
                                color: "#000",
                                fontSize: "1.1rem",
                                fontWeight: "600",
                            }),
                            multiValueRemove: (styles) => ({
                                ...styles,
                                backgroundColor: "#F6F6F6",
                                color: "#000",
                                cursor: "pointer",
                                transition: "all .2s ease-in-out",
                                ":hover": {
                                    backgroundColor: "#E52713",
                                    color: "#fff",
                                },
                            }),
                        }}
                    />
                </div>

                <div className="c-modal__body__buttons">
                    <Button
                        variant={"primary"}
                        onClick={() => {
                            setLoading(true);
                            updateCollectionTrigger(paramObject).then(() => {
                                collectionTrigger().then(() => {
                                    setLoading(false);
                                    dispatch(
                                        setAddCollectionProductsModal({
                                            showPopup: false,
                                        }),
                                    );
                                });
                            });
                        }}
                    >
                        <span>+ {t("add-products-to-collection")}</span>
                    </Button>
                    <Button
                        variant={"secondary"}
                        onClick={() => {
                            dispatch(
                                setAddCollectionProductsModal({
                                    showPopup: false,
                                }),
                            );
                        }}
                    >
                        <span>{t("cancel")}</span>
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
